import React from "react";
import { formatDistance } from "date-fns";

interface NotificationRowProps {
  message: React.ReactNode;
  createdAt: string;
  unread: boolean;
  icon: React.ReactNode;
}

/** A single list row inside the notifications popover: icon, message, date meta and unread dot. */
export const NotificationRow = ({ message, createdAt, unread, icon }: NotificationRowProps) => (
  <div
    className={`relative flex items-start gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-surface-hover ${
      unread ? "bg-primary-soft/50" : ""
    }`}
  >
    <span
      className={`inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${
        unread ? "bg-primary-soft text-primary-text" : "bg-surface-hover text-ink-3"
      }`}
      aria-hidden="true"
    >
      {icon}
    </span>
    <div className="flex-1 min-w-0 pr-3">
      <p className={`text-sm leading-snug break-words ${unread ? "font-medium text-ink" : "text-ink-2"}`}>{message}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1.5 text-xs text-ink-3">
        <span>
          {new Intl.DateTimeFormat("en-US", {
            weekday: "long",
            day: "numeric",
            month: "short",
            year: "numeric",
          }).format(new Date(createdAt))}
        </span>
        <span className="w-1 h-1 rounded-full bg-ink-4" aria-hidden="true" />
        <span>
          {formatDistance(new Date(createdAt), new Date(), { addSuffix: true })
            .replace("about ", "")
            .replace("less than ", "")}
        </span>
      </div>
    </div>
    {unread && (
      <span className="absolute w-2 h-2 rounded-full top-4 right-3 bg-primary" aria-label="Unread" role="img" />
    )}
  </div>
);
